import { useRef, useState, useCallback, useLayoutEffect } from 'react'

import { IS_RTL_DIRECTION } from 'consts'

import * as Styled from './styled'
import FlatTab from './flat-tab'
import { IFlatTabDataWithText } from './interfaces'

interface IProps {
  dataWithTexts?: IFlatTabDataWithText[]
  defaultActiveTabName: string
  isFontPopup?: boolean
  withoutPadding?: boolean
  areHoverStylesUnset?: boolean
  onCurrentItemClick: (name: string) => void
  height?: number
}
// 2 × (3px pill inset + 1px container border) — keep in sync with styled.ts.
const INNER_CHROME_WIDTH = 8

const FlatTabbar: React.FC<IProps> = ({
  dataWithTexts,
  withoutPadding = false,
  areHoverStylesUnset,
  defaultActiveTabName,
  onCurrentItemClick,
  height = 40
}) => {
  const [tabbarWidth, setTabbarWidth] = useState(0)
  const activeTabRef = useRef<null | HTMLDivElement>(null)
  const tabbarRef = useRef<null | HTMLDivElement>(null)

  const handleTabClick = useCallback(
    (name: string) => {
      const activeTabStyleObject = activeTabRef.current?.style

      if (activeTabStyleObject && !activeTabStyleObject.transition) {
        activeTabStyleObject.transition =
          'transform 0.25s ease, width 0.25s ease'
      }
      onCurrentItemClick(name)
    },
    [onCurrentItemClick]
  )

  const data = dataWithTexts || []
  const activeTabIndex = data.findIndex(
    tab => tab.name === defaultActiveTabName
  )

  const tabWidth = (tabbarWidth - INNER_CHROME_WIDTH) / data.length

  const TabList = dataWithTexts?.map(tab => {
    return (
      <FlatTab
        name={tab.name}
        isActive={defaultActiveTabName === tab.name}
        areHoverStylesUnset={areHoverStylesUnset}
        text={tab.text}
        icon={tab.icon}
        tooltipText={tab.tooltipText}
        onClick={handleTabClick}
        tabWidth={tabWidth}
        key={tab.name} // TODO: @gev-davtyan-rf check if you couldn't define unique id for each item
      />
    )
  })

  useLayoutEffect(() => {
    const tabbar = tabbarRef.current

    if (!tabbar) return

    // Tab widths are hard pixels (see tabWidth), so a container-width change
    // without a remount would leave the WHOLE control mis-sized, not just the
    // sliding pill — e.g. a phone rotation while the mobile SubSidebar panel
    // stays mounted. Observe the container instead of measuring once.
    // Safe from a feedback loop: the tabbar is a block-level flex container
    // filling its wrapper, so the children's widths never feed back into it.
    const observer = new ResizeObserver(() =>
      setTabbarWidth(tabbar.offsetWidth)
    )

    observer.observe(tabbar)
    setTabbarWidth(tabbar.offsetWidth)

    return () => observer.disconnect()
  }, [])

  return (
    <Styled.FlatTabbarWrapper
      $height={height}
      $withoutPadding={withoutPadding}
      $isTabbarVisible={!!tabbarWidth}
    >
      <Styled.Tabbar ref={tabbarRef}>
        {TabList}
        {tabbarWidth && (
          <Styled.ActiveTab
            $activeTabIndex={activeTabIndex}
            $isRTLDirection={IS_RTL_DIRECTION}
            $tabWidth={tabWidth}
            ref={activeTabRef}
          />
        )}
      </Styled.Tabbar>
    </Styled.FlatTabbarWrapper>
  )
}

export default FlatTabbar
